import React, { useEffect, useRef, useState } from 'react';
import './TdtMapAutoMarker.css'; // 单独抽离样式，避免scoped问题

// 天地图自动标注组件（React 17.0.1）
const TdtMapAutoMarker = React.forwardRef(({
  mapId = 'tdt-map-container',
  tk, // 必填：天地图tk密钥
  defaultCoord, // 必填：标注坐标 [lng, lat]
  defaultTitle = '我的坐标',
  zoom = 15,
  width = '100%',
  height = '600px',
  onMarkerClick,
  onMapReady,
  onMapError
}, ref) => {
  // 存储地图相关实例（用useRef避免重渲染）
  const mapRef = useRef(null);
  const tRef = useRef(null); // 天地图全局对象T
  const markerRef = useRef(null); // 标注实例
  const labelRef = useRef(null); // 标签实例
  const containerRef = useRef(null); // 地图容器DOM

  // 初始化地图
  useEffect(() => {
    // 校验必填参数
    if (!tk || !defaultCoord || defaultCoord.length !== 2) {
      const err = new Error('tk密钥和defaultCoord坐标为必填项');
      onMapError && onMapError(err);
      console.error('❌ 地图初始化失败：', err);
      return;
    }

    // 延迟初始化，确保天地图API加载完成
    const timer = setTimeout(() => {
      initMap();
    }, 1000);

    // 组件卸载时销毁地图
    return () => {
      clearTimeout(timer);
      destroyMap();
    };
  }, [tk, defaultCoord, zoom, defaultTitle]);

  // 核心：初始化地图逻辑
  const initMap = () => {
    try {
      // 1. 获取天地图全局对象
      const T = window.T;
      if (!T) {
        throw new Error('未找到天地图全局对象 window.T');
      }
      tRef.current = T;

      // 2. 创建地图实例
      const map = new T.Map(mapId);
      if (!map) {
        throw new Error('创建地图实例失败');
      }
      mapRef.current = map;

      // 3. 创建坐标对象
      const centerPoint = new T.LngLat(defaultCoord[0], defaultCoord[1]);
      if (!centerPoint) {
        throw new Error('创建坐标对象失败');
      }

      // 4. 设置地图视角
      map.centerAndZoom(centerPoint, zoom);
      map.enableScrollWheelZoom(true); // 滚轮缩放
      map.enableDrag(true); // 拖拽

      // 5. 加载瓦片图层（修复POI图标问题）
      // loadTileLayerManually(map, tk);

      // 6. 自动添加标注
      addAutoMarker(map, T, centerPoint, defaultTitle);

      onMapReady && onMapReady(map);

    } catch (err) {
      console.error('❌ 地图初始化失败：', err);
      onMapError && onMapError(err);
    }
  };

  // 加载瓦片图层（内置无POI方案）
  const loadTileLayerManually = (map, tk) => {
    const T = tRef.current;

    const tiandituDomain = "https://map.tianditu.gov.cn";
    const vecUrl = `${tiandituDomain}/DataServer?T=vec_w&X={x}&Y={y}&L={z}&tk=${tk}&noproxy=1`;
    const cvaUrl = `${tiandituDomain}/DataServer?T=cva_w&X={x}&Y={y}&L={z}&tk=${tk}&noproxy=1`;

    try {
      // 创建图层
      const vecLayer = new T.TileLayer('vec', { tileUrlTemplate: vecUrl });
      const cvaLayer = new T.TileLayer('cva', { tileUrlTemplate: cvaUrl });
      
      // 先移除旧的注记图层
      if (map.getLayers) {
        const layers = map.getLayers();
        layers.forEach(layer => {
          if (layer.options && layer.options.tileUrlTemplate?.includes('cva')) {
            map.removeLayer(layer);
          }
        });
      }

      // 添加新图层
      map.addLayer(vecLayer);
      map.addLayer(cvaLayer);
    } catch (e) {
      console.warn('图层加载用极简模式：', e);
    }
  };

  // 自动添加标注
  const addAutoMarker = (map, T, centerPoint, title) => {
    // 1. 创建标记
    const marker = new T.Marker(centerPoint, { title });
    markerRef.current = marker;

    // 兼容添加标记到地图
    if (map.addOverlay) {
      map.addOverlay(marker);
    } else if (map.addOverLay) {
      map.addOverLay(marker);
    } else if (map.add) {
      map.add(marker);
    }

    // 2. 创建标签
    const label = new T.Label({ text: title, position: centerPoint });
    labelRef.current = label;

    // 兼容添加标签到地图
    if (map.addOverlay) {
      map.addOverlay(label);
    } else if (map.addOverLay) {
      map.addOverLay(label);
    } else if (map.add) {
      map.add(label);
    }

    // 标注点击事件
    if (marker.addEventListener) {
      marker.addEventListener('click', () => {
        onMarkerClick && onMarkerClick({
          lng: defaultCoord[0],
          lat: defaultCoord[1],
          title
        });
      });
    }
  };

  // 销毁地图
  const destroyMap = () => {
    const map = mapRef.current;
    const marker = markerRef.current;
    const label = labelRef.current;

    // 移除标记/标签
    if (marker && map?.removeOverlay) {
      map.removeOverlay(marker);
    }
    if (label && map?.removeOverlay) {
      map.removeOverlay(label);
    }

    // 销毁地图实例
    if (map?.destroy) {
      map.destroy();
    }

    // 清空ref
    mapRef.current = null;
    tRef.current = null;
    markerRef.current = null;
    labelRef.current = null;
  };

  // 扩展方法：更新标注坐标（可通过ref调用）
  const updateMarkerCoord = (newCoord) => {
    const map = mapRef.current;
    const marker = markerRef.current;
    const label = labelRef.current;
    const T = tRef.current;

    if (!map || !marker || !label || !T) return;

    const newPoint = new T.LngLat(newCoord[0], newCoord[1]);
    if (marker.setPosition) {
      marker.setPosition(newPoint);
    }
    if (label.setPosition) {
      label.setPosition(newPoint);
    }
    if (map.panTo) {
      map.panTo(newPoint);
    }
  };

  // 暴露方法给父组件（通过ref）
  useEffect(() => {
    const currentRef = ref || containerRef;
    if (currentRef.current) {
      currentRef.current.updateMarkerCoord = updateMarkerCoord;
    }
  }, [ref]);

  return (
    <div
      ref={ref || containerRef}
      id={mapId}
      className="tdt-map-container"
      style={{ width, height, position: 'relative', overflow: 'hidden', minHeight: '300px' }}
    />
  );
});

export default TdtMapAutoMarker;